import { useState } from "react";
import { Icon } from "./Icon";
import { Modal } from "./Modal";

interface Props {
  clientId: string;
  clientSecret: string;
  mode: "issued" | "revealed";
  onClear: () => void;
}

export function SecretDialog({ clientId, clientSecret, mode, onClear }: Props) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const revealed = mode === "revealed";

  async function copy() {
    try {
      await navigator.clipboard.writeText(clientSecret);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
    window.setTimeout(() => setCopyState("idle"), 1800);
  }

  return (
    <Modal
      closeDisabled
      eyebrow={revealed ? "Sensitive credential" : "One-time credential"}
      title={revealed ? "Client secret" : "Save the client secret"}
      titleId="secret-dialog-title"
    >
      <div className="secret-dialog__body">
        <div className="notice notice--warning">
          {revealed ? (
            <>
              <strong>Secret access recorded.</strong> This reveal was written
              to the audit log. Clear it from the screen when finished.
            </>
          ) : (
            <>
              This value will not be available again. If it is lost, rotate the
              secret and update the relying party.
            </>
          )}
        </div>
        <label>
          <span>Client ID</span>
          <div className="copy-value">
            <code>{clientId}</code>
          </div>
        </label>
        <label>
          <span>Client secret</span>
          <div className="copy-value copy-value--secret">
            <code>{clientSecret}</code>
            <button
              className="button button--secondary"
              onClick={() => void copy()}
              type="button"
            >
              <Icon
                name={
                  copyState === "copied"
                    ? "check"
                    : copyState === "failed"
                      ? "warning"
                      : "copy"
                }
                size={16}
              />
              {copyState === "copied"
                ? "Copied"
                : copyState === "failed"
                  ? "Copy failed"
                  : "Copy"}
            </button>
          </div>
        </label>
        <button
          className="button button--primary button--large"
          onClick={onClear}
          type="button"
        >
          {revealed ? "Clear secret from screen" : "I saved it — clear secret"}
        </button>
      </div>
    </Modal>
  );
}
